import React, { useState } from "react";
import toast from "react-hot-toast";

import { invoicesApi } from "../../api";
import { useSubmitLock } from "../../hooks/useSubmitLock";
import { generateInvoicePdfBlob } from "../../utils/invoicePrintDocument";
import type { InvoiceRecord } from "../../types";
import "./EmailInvoiceModal.css";

type Props = {
  invoice: InvoiceRecord;
  token?: string | null;
  onClose: () => void;
  onSent: (updated: InvoiceRecord) => void;
};

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function formatSentStamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  return `on ${day}/${month}/${year} ${time}`;
}

export const EmailInvoiceModal: React.FC<Props> = ({ invoice, token, onClose, onSent }) => {
  const [email, setEmail] = useState(invoice.sent_to ?? "");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(invoice.sent_to ?? null);
  const [sentAt, setSentAt] = useState<string | null>(invoice.sent_at ?? null);
  const { run } = useSubmitLock();

  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    await run(async () => {
      if (!token || !email.trim()) return;
      setSending(true);
      try {
        const blob = await generateInvoicePdfBlob(invoice);
        const base64 = await blobToBase64(blob);
        const updated = await invoicesApi.email(invoice.id, email.trim(), base64, token);
        setSentTo(updated.sent_to ?? email.trim());
        setSentAt(updated.sent_at ?? new Date().toISOString());
        onSent(updated);
        toast.success(`Invoice emailed to ${email.trim()}`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not send invoice");
      } finally {
        setSending(false);
      }
    });
  };

  return (
    <div className="email-invoice-modal" role="presentation" onMouseDown={handleClose}>
      <div
        className="email-invoice-modal__card"
        role="dialog"
        aria-modal="true"
        aria-label={sentTo ? "Email sent" : "Enter email"}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {sentTo ? (
          <div className="email-invoice-modal__state">
            <div className="email-invoice-modal__title">Email Sent to</div>
            <input className="email-invoice-modal__field" value={sentTo} readOnly />
            <p className="email-invoice-modal__stamp">{sentAt ? formatSentStamp(sentAt) : ""}</p>
            <button
              type="button"
              className="email-invoice-modal__again"
              onClick={() => {
                setSentTo(null);
                setSentAt(null);
              }}
            >
              Send again
            </button>
          </div>
        ) : (
          <form className="email-invoice-modal__state" onSubmit={handleSend}>
            <div className="email-invoice-modal__title">Enter Email</div>
            <input
              className="email-invoice-modal__field"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              required
              autoFocus
              disabled={sending}
            />
            <button className="email-invoice-modal__send" type="submit" disabled={sending}>
              {sending ? "Sending…" : "Send"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default EmailInvoiceModal;
